import { useState, type FormEvent } from "react";
import { useAuth } from "@/contexts/AuthContext";
import LanguagePicker from "@/components/LanguagePicker";
import { useT } from "@/i18n";

export default function LoginPage() {
  const { signIn } = useAuth();
  const { t } = useT();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await signIn(email, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("login.failed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center px-4">
      <form onSubmit={onSubmit} className="w-full max-w-sm">
        <div className="mb-6 flex justify-end"><LanguagePicker /></div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">{t("login.title")}</h1>
        <p className="mt-1 mb-8 text-sm text-slate-500">{t("login.intro")}</p>

        <label className="block text-sm font-medium text-slate-700" htmlFor="email">{t("login.email")}</label>
        <input
          id="email" type="email" value={email} required autoFocus
          autoComplete="username" inputMode="email"
          onChange={(e) => setEmail(e.target.value)}
          className="mt-1 mb-4 w-full rounded-lg border border-slate-300 px-3 py-2 text-base shadow-sm focus:border-slate-900 focus:outline-none focus:ring-1 focus:ring-slate-900"
        />

        <label className="block text-sm font-medium text-slate-700" htmlFor="password">{t("login.password")}</label>
        <input
          id="password" type="password" value={password} required
          autoComplete="current-password"
          onChange={(e) => setPassword(e.target.value)}
          className="mt-1 mb-6 w-full rounded-lg border border-slate-300 px-3 py-2 text-base shadow-sm focus:border-slate-900 focus:outline-none focus:ring-1 focus:ring-slate-900"
        />

        {/* role=alert so a screen reader announces the failure; the field keeps focus for a retry. */}
        {error && (
          <p role="alert" className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
        )}

        <button
          type="submit" disabled={busy}
          className="w-full rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-700 disabled:opacity-50"
        >
          {busy ? t("login.busy") : t("login.submit")}
        </button>

        <p className="mt-6 text-xs text-slate-400">
          {t("login.noSignup")}
        </p>
      </form>
    </main>
  );
}
